/* eslint-disable no-param-reassign */
import { createRouter, createWebHistory } from 'vue-router';
import { ref } from 'vue';

import usePageService from '@/services/pages';
import usePollsService from '@/services/polls';

const routes = [
  {
    path: '/',
    name: 'site-root',
    component: () => import('@/views/NotFoundView.vue'), // no base page so 404 for now
    meta: {},
  },
  {
    path: '/:pageId', // as rid
    props: true,
    name: 'landing-page',
    component: () => import('@/views/LandingPageView.vue'),
    beforeEnter: [getPageData],
    meta: {
      title: 'Landing Page',
    },
  },
  {
    path: '/preview/:pageId', // as id
    props: true,
    name: 'preview-landing-page',
    component: () => import('@/views/LandingPageView.vue'),
    beforeEnter: [getPreviewData],
    meta: {
      title: 'Preview Landing Page',
      preview: true,
    },
  },
  {
    path: '/edit',
    name: 'edit-landing-page',
    component: () => import('@/views/LandingPageView.vue'),
    meta: {
      title: 'Edit Landing Page',
      editing: true,
    },
  },
  {
    path: '/display/edit',
    name: 'edit-display',
    component: () => import('@/views/DisplayView.vue'),
    meta: {
      title: 'Preview Display',
      editing: true,
    },
  },
  {
    path: '/display/live-output',
    name: 'live-output',
    component: () => import('@/views/DisplayView.vue'),
    meta: {
      title: 'Live Output Display Render',
      live: true,
    },
  },
  {
    path: '/poll/:pollId/display/:displayId',
    name: 'poll-display',
    component: () => import('@/views/DisplayView.vue'),
    beforeEnter: [getPollData],
    meta: {
      title: 'Poll Display Render',
      live: true,
    },
  },
  {
    path: '/server-error',
    name: 'server-error',
    component: () => import('@/views/EndUserGenericErrorView.vue'),
    meta: {
      title: 'Server Error',
    },
  },
  {
    path: '/:pathMatch(.*)*',
    alias: '/not-found',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: {
      title: 'Page Not Found',
    },
  },
];

export const isLoading = ref(false);
export const isLiveDisplay = ref(false);

const pageService = usePageService();
const pollService = usePollsService();

const errorNavigation = (next, payload) => {
  if (payload.response?.status === 404) {
    next({ name: 'not-found', params: { pathMatch: ['not-found'] } });
  }

  if (payload.response?.status === 500) {
    next({ name: 'server-error' });
  }

  if (!payload.data) {
    next({ name: 'not-found' });
  }
};

const getPageOptions = (next) => ({
  initialData: {},
  immediate: true,
  onFetchError(payload) {
    errorNavigation(next, payload);
  },
});

/**
 * Fetches poll data and sets it in the route params.
 * Functions like a middleware to ensure data is loaded before we even mount
 * a view.
 */
async function getPollData(to, from, next) {
  const { pollId, displayId } = to.params;

  // If pollId is in route params, we'll fetch and render display found on that poll.
  try {
    const { data } = await pollService.getPollById(pollId, { displayId });
    to.params.pollData = data.value;
    to.params.fontData = data.value?.fonts || [];
  } catch (err) {
    console.error('Error fetching poll data:', pollId, err);
  }

  next();
}

async function getPageData(to, from, next) {
  const { pageId } = to.params;

  const { data } = await pageService.getPage(pageId, getPageOptions(next));

  to.params.pageData = data.value.page;
  to.params.fontsData = data.value.fonts;
  to.params.affiliateData = data.value.affiliates;

  next();
}

async function getPreviewData(to, from, next) {
  const { pageId } = to.params;

  const { data } = await pageService.getPagePreview(pageId, getPageOptions(next));

  to.params.pageData = data.value.page;
  to.params.fontsData = data.value.fonts;
  to.params.affiliateData = data.value.affiliates;

  next();
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach((to) => {
  isLoading.value = true;
  isLiveDisplay.value = to.meta.live || false;

  const { title, description } = to.meta;
  // if no title is set in meta.title, set the default title
  const defaultTitle = 'Tagboard Landing Page';
  const defaultDescription = 'A simple landing page for your tagboard creations.';

  document.title = title || defaultTitle;

  const descriptionElement = document.querySelector('head meta[name="description"]');

  descriptionElement.setAttribute('content', description || defaultDescription);
});

router.afterEach(() => {
  isLoading.value = false;
});

export default router;
